import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, AreaChart, Area } from 'recharts';
import { hcGetStatistics, hcExportCrqsStatistics } from '../actions/helixCrqsThunks';
import './HelixCrqs.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── COLOURS PER STATUS ───────────────────────────────────────────────────────
const STATUS_COLORS = {
  Conflits:  '#ef4444',
  Approved:  '#16a34a',
  Completed: '#1d4ed8',
  Cancelled: '#d97706',
  Closed:    '#7c3aed',
  Created:   '#0e7490',
};

const getColor = (status) => STATUS_COLORS[status] ?? '#94a3b8';

// ─── CUSTOM TOOLTIPS ──────────────────────────────────────────────────────────
function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { crqStatus, total } = payload[0].payload;
  return (
    <div style={{
      background: '#0f172a', border: 'none', borderRadius: 10,
      padding: '10px 16px', fontFamily: "'Inter', sans-serif", fontSize: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    }}>
      <p style={{ fontWeight: 700, color: '#f1f5f9', marginBottom: 4, letterSpacing: '0.02em' }}>{crqStatus}</p>
      <p style={{ color: '#94a3b8' }}>Total: <strong style={{ color: getColor(crqStatus), fontSize: 14 }}>{total?.toLocaleString()}</strong></p>
    </div>
  );
}

function LineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0f172a', border: 'none', borderRadius: 10,
      padding: '10px 16px', fontFamily: "'Inter', sans-serif", fontSize: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    }}>
      <p style={{ fontWeight: 700, color: '#94a3b8', marginBottom: 4, fontSize: 11 }}>{label}</p>
      <p style={{ color: '#f1f5f9' }}>Total: <strong style={{ color: '#60a5fa', fontSize: 14 }}>{payload[0]?.value?.toLocaleString()}</strong></p>
    </div>
  );
}

// ─── PER DAY TABLE ────────────────────────────────────────────────────────────
const PER_DAY_PAGE_SIZE = 10;

function PerDayTable({ rows, loading }) {
  const [page, setPage] = useState(1);

  // ── date range for chart ─────────────────────────────────────────────────
  const totalDays  = rows.length;
  const sortedRows = useMemo(() => [...rows].sort((a, b) => (a.day > b.day ? 1 : -1)), [rows]);

  // default: show last 30 days
  const defaultStart = useMemo(() => Math.max(0, totalDays - 30), [totalDays]);
  const [range, setRange] = useState([defaultStart, totalDays]);

  // update range when rows load
  useEffect(() => {
    setRange([Math.max(0, totalDays - 30), totalDays]);
  }, [totalDays]);

  const chartRows  = useMemo(() => sortedRows.slice(range[0], range[1]), [sortedRows, range]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PER_DAY_PAGE_SIZE));
  const paged      = rows.slice((page - 1) * PER_DAY_PAGE_SIZE, page * PER_DAY_PAGE_SIZE);

  const startLabel = sortedRows[range[0]]?.day ?? '';
  const endLabel   = sortedRows[range[1] - 1]?.day ?? '';

  return (
    <div className="hc-card">
      <div className="hc-card-header">
        <span className="hc-card-title">Statistics Per Day</span>
        <span className="hc-badge" style={{ background: '#f0fdf4', color: '#16a34a' }}>Per Day</span>
      </div>
      <div className="hc-card-body" style={{ padding: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, alignItems: 'start' }}>

          {/* Table */}
          <div style={{ borderRight: '1px solid #f1f5f9' }}>
            <div className="hc-table-wrapper" style={{ height: 'auto' }}>
              <table className="hc-table" style={{ fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '5px 10px' }}>Day</th>
                    <th style={{ padding: '5px 10px' }}>Total</th>
                    <th style={{ padding: '5px 10px' }}>Created</th>
                    <th style={{ padding: '5px 10px' }}>Closed</th>
                    <th style={{ padding: '5px 10px' }}>Approved</th>
                    <th style={{ padding: '5px 10px' }}>Completed</th>
                    <th style={{ padding: '5px 10px' }}>Cancelled</th>
                    <th style={{ padding: '5px 10px' }}>Conflits</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="hc-table-empty"><span className="hc-spinner" style={{ marginRight: 8 }} />Loading...</td></tr>
                  ) : paged.length === 0 ? (
                    <tr><td colSpan={8} className="hc-table-empty">No data available</td></tr>
                  ) : paged.map((row, idx) => (
                    <tr key={row.day ?? idx}>
                      <td style={{ padding: '5px 10px' }}>{row.day}</td>
                      <td style={{ padding: '5px 10px' }}>{row.total?.toLocaleString()}</td>
                      <td style={{ padding: '5px 10px' }}>{row.totalCreated?.toLocaleString()}</td>
                      <td style={{ padding: '5px 10px' }}>{row.totalClosed?.toLocaleString()}</td>
                      <td style={{ padding: '5px 10px' }}>{row.totalApproved?.toLocaleString()}</td>
                      <td style={{ padding: '5px 10px' }}>{row.totalCompleted?.toLocaleString()}</td>
                      <td style={{ padding: '5px 10px' }}>{row.totalCancelled?.toLocaleString()}</td>
                      <td style={{ padding: '5px 10px' }}>{row.totalConflits?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && rows.length > PER_DAY_PAGE_SIZE && (
              <div className="hc-pagination">
                <button className="hc-page-btn" onClick={() => setPage(1)}                disabled={page === 1}>«</button>
                <button className="hc-page-btn" onClick={() => setPage((p) => p - 1)}    disabled={page === 1}>‹</button>
                <span className="hc-page-info">{page} / {totalPages} <span className="hc-page-total">({rows.length})</span></span>
                <button className="hc-page-btn" onClick={() => setPage((p) => p + 1)}    disabled={page === totalPages}>›</button>
                <button className="hc-page-btn" onClick={() => setPage(totalPages)}      disabled={page === totalPages}>»</button>
              </div>
            )}
          </div>

          {/* Line chart with range slider */}
          <div style={{ padding: '20px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, background: '#f8fafc', borderRadius: '0 0 0 12px' }}>
            {loading || rows.length === 0 ? (
              <div style={{ width: '100%', textAlign: 'center', color: '#cbd5e1', fontFamily: "'Inter', sans-serif", fontSize: 13, fontStyle: 'italic' }}>
                {loading ? 'Loading…' : 'No data available'}
              </div>
            ) : (
              <>
                {/* Date range label */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, fontSize: 12, color: '#0f172a' }}>Totals</span>
                  <span style={{ color: '#94a3b8' }}>{chartRows.length} days</span>
                </div>

                {/* Chart */}
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartRows} margin={{ top: 4, right: 8, left: 0, bottom: 24 }}>
                    <defs>
                      <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fill: '#94a3b8' }}
                      angle={-35}
                      textAnchor="end"
                      interval="preserveStartEnd"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                      width={36}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<LineTooltip />} cursor={{ stroke: '#3b82f6', strokeWidth: 1, strokeDasharray: '4 4' }} />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="#3b82f6"
                      strokeWidth={2.5}
                      fill="url(#totalGradient)"
                      dot={false}
                      activeDot={{ r: 5, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>

                {/* Range slider — start only */}
                <div style={{ padding: '0 4px' }}>
                  <input
                    type="range"
                    min={0}
                    max={totalDays - 1}
                    value={range[0]}
                    onChange={(e) => setRange([Number(e.target.value), totalDays])}
                    style={{ width: '100%', accentColor: '#1d4ed8', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#94a3b8' }}>
                      Drag to adjust start date
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── PER DAY USER TABLE ───────────────────────────────────────────────────────
function PerDayUserTable({ rows, loading }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PER_DAY_PAGE_SIZE));
  const paged      = rows.slice((page - 1) * PER_DAY_PAGE_SIZE, page * PER_DAY_PAGE_SIZE);

  return (
    <div className="hc-card">
      <div className="hc-card-header">
        <span className="hc-card-title">Statistics Per Day &amp; User</span>
        <span className="hc-badge" style={{ background: '#faf5ff', color: '#7c3aed' }}>Per Day &amp; User</span>
      </div>
      <div className="hc-card-body" style={{ padding: 0 }}>
        <div className="hc-table-wrapper" style={{ height: 'auto' }}>
          <table className="hc-table" style={{ fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ padding: '5px 10px' }}>Day</th>
                <th style={{ padding: '5px 10px' }}>User</th>
                <th style={{ padding: '5px 10px' }}>Created</th>
                <th style={{ padding: '5px 10px' }}>Closed</th>
                <th style={{ padding: '5px 10px' }}>Approved</th>
                <th style={{ padding: '5px 10px' }}>Completed</th>
                <th style={{ padding: '5px 10px' }}>Cancelled</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="hc-table-empty"><span className="hc-spinner" style={{ marginRight: 8 }} />Loading...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} className="hc-table-empty">No data available</td></tr>
              ) : paged.map((row, idx) => (
                <tr key={`${row.day}-${row.user}-${idx}`}>
                  <td style={{ padding: '5px 10px' }}>{row.day}</td>
                  <td style={{ padding: '5px 10px' }}>{row.user}</td>
                  <td style={{ padding: '5px 10px' }}>{row.totalCreated?.toLocaleString()}</td>
                  <td style={{ padding: '5px 10px' }}>{row.totalClosed?.toLocaleString()}</td>
                  <td style={{ padding: '5px 10px' }}>{row.totalApproved?.toLocaleString()}</td>
                  <td style={{ padding: '5px 10px' }}>{row.totalCompleted?.toLocaleString()}</td>
                  <td style={{ padding: '5px 10px' }}>{row.totalCancelled?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > PER_DAY_PAGE_SIZE && (
          <div className="hc-pagination">
            <button className="hc-page-btn" onClick={() => setPage(1)}             disabled={page === 1}>«</button>
            <button className="hc-page-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
            <span className="hc-page-info">{page} / {totalPages} <span className="hc-page-total">({rows.length})</span></span>
            <button className="hc-page-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
            <button className="hc-page-btn" onClick={() => setPage(totalPages)}   disabled={page === totalPages}>»</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function HelixStatistics() {
  const classes    = useStyles();
  const dispatch   = useDispatch();
  const loading    = useSelector((state) => state.helixCrqsReducer.loading);
  const statistics = useSelector((state) => state.helixCrqsReducer.statistics);
  const totals     = statistics?.helixCrqsStatisticsTotals ?? [];

  useEffect(() => {
    dispatch(hcGetStatistics());
  }, []);

  return (
    <Page className={classes.root} title="Helix Statistics">
      <Header module="NASP" view="Helix Statistics" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="hc-root">
            <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── Mini Panel ─────────────────────────────────────────────── */}
              <div className="hc-card">
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', fontFamily: "'Inter', sans-serif" }}>Options</span>
                  <button
                    className="hc-dropdown-trigger"
                    onClick={() => dispatch(hcExportCrqsStatistics())}
                    disabled={loading.exportCrqsStatistics}
                    style={{ gap: 6, color: '#16a34a', borderColor: '#bbf7d0' }}
                  >
                    {loading.exportCrqsStatistics
                      ? <span className="hc-spinner" style={{ width: 12, height: 12 }} />
                      : <span style={{ fontSize: 14 }}>↓</span>
                    }
                    Export
                  </button>
                </div>
              </div>

              {/* ── Totals ── */}
              <div className="hc-card">
                <div className="hc-card-header">
                  <span className="hc-card-title">Totals</span>
                  <span className="hc-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>Statistics</span>
                </div>
                <div className="hc-card-body" style={{ padding: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, alignItems: 'start' }}>

                    {/* Table */}
                    <div className="hc-table-wrapper" style={{ height: 'auto', borderRight: '1px solid #f1f5f9' }}>
                      <table className="hc-table" style={{ fontSize: 11 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '5px 10px' }}>CRQ Status</th>
                            <th style={{ padding: '5px 10px' }}>Total</th>
                            <th style={{ padding: '5px 10px' }}>Total Time</th>
                            <th style={{ padding: '5px 10px' }}>Avg Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading.statistics ? (
                            <tr>
                              <td colSpan={4} className="hc-table-empty">
                                <span className="hc-spinner" style={{ marginRight: 8 }} />
                                Loading...
                              </td>
                            </tr>
                          ) : totals.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="hc-table-empty">No data available</td>
                            </tr>
                          ) : totals.map((row, idx) => (
                            <tr key={row.crqStatus ?? idx}>
                              <td style={{ padding: '5px 10px' }}>
                                <span style={{
                                  display: 'inline-block', width: 8, height: 8,
                                  borderRadius: '50%', background: getColor(row.crqStatus),
                                  marginRight: 8, flexShrink: 0,
                                }} />
                                {row.crqStatus}
                              </td>
                              <td style={{ padding: '5px 10px' }}>{row.total?.toLocaleString()}</td>
                              <td style={{ padding: '5px 10px' }}>{row.totalTime}</td>
                              <td style={{ padding: '5px 10px' }}>{row.avgTime}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Bar chart */}
                    <div style={{ padding: '20px 16px 12px', display: 'flex', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 12px 0' }}>
                      {loading.statistics || totals.length === 0 ? (
                        <div style={{ width: '100%', textAlign: 'center', color: '#cbd5e1', fontFamily: "'Inter', sans-serif", fontSize: 13, fontStyle: 'italic' }}>
                          {loading.statistics ? 'Loading…' : 'No data available'}
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={totals} margin={{ top: 8, right: 16, left: 0, bottom: 40 }} barCategoryGap="35%">
                            <defs>
                              {totals.map((row) => (
                                <linearGradient key={row.crqStatus} id={`grad-${row.crqStatus}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={getColor(row.crqStatus)} stopOpacity={1} />
                                  <stop offset="100%" stopColor={getColor(row.crqStatus)} stopOpacity={0.6} />
                                </linearGradient>
                              ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis
                              dataKey="crqStatus"
                              tick={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fill: '#64748b', fontWeight: 500 }}
                              angle={-35}
                              textAnchor="end"
                              interval={0}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fill: '#94a3b8' }}
                              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                              width={40}
                              axisLine={false}
                              tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148,163,184,0.08)', radius: 6 }} />
                            <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={48}>
                              {totals.map((row) => (
                                <Cell key={row.crqStatus} fill={`url(#grad-${row.crqStatus})`} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                  </div>
                </div>
              </div>

              {/* ── Statistics Per Day ── */}
              <PerDayTable rows={statistics?.helixCrqsStatisticsPerDay ?? []} loading={loading.statistics} />

              {/* ── Statistics Per Day & User ── */}
              <PerDayUserTable rows={statistics?.helixCrqsStatisticsPerDayUser ?? []} loading={loading.statistics} />

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default HelixStatistics;